import React from 'react';

import { Text } from 'components/Text';
import { getSocialNetworkIcon } from 'components/UserProfileCard/utils/socialNetworkIcons';
import { useTheme } from 'hooks/useTheme';
import { cn } from 'lib/utils';

interface SocialIconsContainerProps {
  socialNetworks: any[];
  socialLoading?: boolean;
  socialError?: string | null;
  color?: string;
  circle?: boolean;
  iconSize?: number;
  useThemeColor?: boolean;
  themeColorKey?: 'primary' | 'success' | 'warning' | 'info' | 'error';
  gap?: string;
  justifyContent?: string;
  alignItems?: string;
  className?: string;
}

// Map gap values to Tailwind classes
const getGapClass = (gap: string): string => {
  const gapMap: Record<string, string> = {
    '5px': 'gap-1',
    '10px': 'gap-2.5',
    '15px': 'gap-4',
    '20px': 'gap-5',
  };
  return gapMap[gap] || 'gap-2.5';
};

// Map justify-content values to Tailwind classes
const getJustifyClass = (justify: string): string => {
  const justifyMap: Record<string, string> = {
    center: 'justify-center',
    'flex-start': 'justify-start',
    'flex-end': 'justify-end',
    'space-between': 'justify-between',
    'space-around': 'justify-around',
  };
  return justifyMap[justify] || 'justify-center';
};

// Map align-items values to Tailwind classes
const getAlignClass = (align: string): string => {
  const alignMap: Record<string, string> = {
    center: 'items-center',
    'flex-start': 'items-start',
    'flex-end': 'items-end',
    stretch: 'items-stretch',
  };
  return alignMap[align] || 'items-center';
};

const SocialIconsContainer: React.FC<SocialIconsContainerProps> = ({
  socialNetworks,
  socialLoading = false,
  socialError = null,
  color,
  circle = false,
  iconSize = 12,
  useThemeColor = false,
  themeColorKey = 'primary',
  gap = '10px',
  justifyContent = 'center',
  alignItems = 'center',
  className,
}) => {
  const { theme } = useTheme();

  // Build icon wrapper styles based on circle prop
  const getIconWrapperStyle = (): React.CSSProperties => {
    if (circle) {
      return {
        backgroundColor: theme.primary,
        color: theme.background.primary,
      };
    }
    return {
      color: color === 'primary' ? theme.primary : undefined,
    };
  };

  return (
    <div
      className={cn(
        'flex flex-wrap',
        getGapClass(gap),
        getJustifyClass(justifyContent),
        getAlignClass(alignItems),
        className
      )}
    >
      {socialLoading && <Text>Carregando redes sociais...</Text>}
      {socialError && <Text color="hsl(var(--error))">{socialError}</Text>}

      {!socialLoading &&
        !socialError &&
        socialNetworks
          .filter(network => network.social_network_username)
          .map(network => {
            // For WEBSITE type, use the username as direct URL, otherwise construct social media URL
            const url =
              network.social_network_type === 'WEBSITE'
                ? network.social_network_username.startsWith('http')
                  ? network.social_network_username
                  : `https://${network.social_network_username}`
                : `https://${network.social_network_type.toLowerCase()}.com/${network.social_network_username}`;

            return (
              <a
                key={network.id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <div
                  className={cn(
                    'flex items-center justify-center cursor-pointer transition-all duration-200 ease-in-out hover:scale-110 hover:opacity-90',
                    circle && 'w-8 h-8 rounded-full'
                  )}
                  style={getIconWrapperStyle()}
                >
                  {getSocialNetworkIcon(network.social_network_type, {
                    color: circle ? theme.background.primary : color,
                    size: iconSize,
                    useThemeColor,
                    themeColorKey,
                  })}
                </div>
              </a>
            );
          })}
    </div>
  );
};

export default SocialIconsContainer;
